import { useMemo, useState } from "react";
import {
  History,
  ListChecks,
  MoreHorizontal,
  Plus,
  Upload,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useActualizeazaTargetareLista } from "@/hooks/api/mutate/useActualizeazaTargetareLista";
import { useSalveazaTargetareListaFisier } from "@/hooks/api/mutate/useSalveazaTargetareListaFisier";
import { useStergeTargetareListaFisier } from "@/hooks/api/mutate/useStergeTargetareListaFisier";
import { useGetTargetareListaPropuneri } from "@/hooks/api/query/useGetTargetareListaPropuneri";
import { useGetTargetareListe } from "@/hooks/api/query/useGetTargetareListe";
import { useToast } from "@/hooks/use-toast";
import type {
  TargetareListaFisier,
  TargetareTipLista,
} from "@/types/targetare";
import { ETICHETE_SURSA_VERSIUNE } from "@/utils/targetareConfig";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import {
  definitieRaportListe,
  randuriListe,
} from "@/utils/targetareRaportListe";
import {
  FirmeCriteriuDialog,
  type FirmeCriteriuDeschis,
} from "../components/FirmeCriteriuDialog";
import { HeroPagina } from "../components/HeroPagina";
import { IstoricModificari } from "../components/IstoricModificari";
import { AdaugaFirmaListaDialog } from "../components/liste/AdaugaFirmaListaDialog";
import { celulaLista } from "../components/liste/CelulaLista";
import { DetaliiListaDialog } from "../components/liste/DetaliiListaDialog";
import { PropuneriPotrivireDialog } from "../components/liste/PropuneriPotrivireDialog";
import {
  ImportListaDialog,
  type ImportListaInitial,
} from "../components/liste/ImportListaDialog";
import { VersiuneListaDialog } from "../components/liste/VersiuneListaDialog";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";

type ListeTargetareProps = {
  // Filtreaza listele afisate dupa tip; folosit de paginile "Liste oficiale" / "Liste neoficiale"
  // din Actualizare date (D14: acelasi ecran, nu o pagina noua duplicata).
  tipFiltru?: TargetareTipLista;
};

export default function ListeTargetare({ tipFiltru }: ListeTargetareProps) {
  const { toast } = useToast();
  const liste = useGetTargetareListe();
  const salveaza = useSalveazaTargetareListaFisier();
  const actualizeaza = useActualizeazaTargetareLista();
  const sterge = useStergeTargetareListaFisier();
  const [firme, setFirme] = useState<FirmeCriteriuDeschis | null>(null);
  const [importDialog, setImportDialog] = useState<{
    deschis: boolean;
    initial: ImportListaInitial | null;
  }>({ deschis: false, initial: null });
  const [adauga, setAdauga] = useState(false);
  const [versiune, setVersiune] = useState<number | null>(null);
  const [deSters, setDeSters] = useState<TargetareListaFisier | null>(null);
  const [detalii, setDetalii] = useState<TargetareListaFisier | null>(null);
  const [propuneri, setPropuneri] = useState(false);
  const propuneriDate = useGetTargetareListaPropuneri();
  const nrPropuneri = propuneriDate.data?.propuneri.length ?? 0;
  const [istoric, setIstoric] = useState(false);

  const date = liste.data;
  const fisiere = useMemo(() => {
    const toate = date?.fisiere ?? [];
    return tipFiltru ? toate.filter((f) => f.tip === tipFiltru) : toate;
  }, [date, tipFiltru]);
  const servicii = useMemo(() => date?.servicii ?? [], [date]);
  const randuri = useMemo(() => randuriListe(fisiere), [fisiere]);
  const sumar = date?.sumar;
  const actualizeazaPeServer = (f: TargetareListaFisier) =>
    actualizeaza.mutate(
      { nume_fisier: f.nume_fisier },
      {
        onSuccess: () =>
          toast({
            title: "Actualizarea rulează pe server",
            description: `${f.nume_fisier}: lista se preia de la sursă și se înlocuiește în fundal; starea apare în coloana „Ultima rulare automată”.`,
          }),
        onError: (e) =>
          toast({
            title: "Actualizarea nu a pornit",
            description: e.message,
            variant: "destructive",
          }),
      }
    );

  const celula = useMemo(
    () =>
      celulaLista({
        servicii,
        inCurs: salveaza.isPending,
        onFirme: setFirme,
        onSalveaza: (patch) =>
          salveaza.mutate(patch, {
            onSuccess: (r) =>
              toast({
                title: "Lista a fost actualizată",
                description: `${r.data.nume_fisier}: ${formatNumar(r.data.potrivite)} rânduri potrivite. Reîncadrarea aplică schimbarea în tabelul de firme.`,
              }),
            onError: (e) =>
              toast({
                title: "Salvarea a eșuat",
                description: e.message,
                variant: "destructive",
              }),
          }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [servicii, salveaza.isPending]
  );

  const confirmaStergerea = () => {
    if (!deSters) return;
    sterge.mutate(deSters.nume_fisier, {
      onSuccess: (r) =>
        toast({
          title: "Lista a fost ștearsă",
          description: `${formatNumar(r.data.sterse)} rânduri eliminate.`,
        }),
      onError: (e) =>
        toast({
          title: "Ștergerea a eșuat",
          description: e.message,
          variant: "destructive",
        }),
    });
    setDeSters(null);
  };

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu={
          tipFiltru === "OFICIALA"
            ? "Listele oficiale"
            : tipFiltru === "NEOFICIALA"
              ? "Listele neoficiale"
              : "Listele oficiale și neoficiale"
        }
        descriere={
          <>
            Fiecare listă (o foaie dintr-un fișier importat) are un serviciu
            NIS2, un tip, o sursă și un comutator activ/inactiv. Listele se
            actualizează din fișier, direct de la adresa sursei sau din text,
            iar fiecare actualizare păstrează data, cine a făcut-o și
            diferențele (firme adăugate și eliminate). Recomandare: actualizare
            lunară a listelor cu sursă; cele mai vechi de 30 de zile sunt
            marcate. Listele cu „actualizare automată” se reiau singure de la
            sursă (pe server, o dată pe lună, câte trei pe zi la 03:15), cu
            aceleași diferențe și versiuni ca importul manual.
          </>
        }
        isLoading={liste.isLoading}
        coloane={4}
        tiles={[
          {
            eticheta: "Liste",
            valoare: sumar?.fisiere,
            detaliu: sumar ? `${formatNumar(sumar.active)} active` : undefined,
          },
          {
            eticheta: "Oficiale / neoficiale",
            valoare: sumar
              ? `${formatNumar(sumar.oficiale)} / ${formatNumar(sumar.neoficiale)}`
              : "–",
          },
          { eticheta: "Cu sursă cunoscută", valoare: sumar?.cu_sursa },
          { eticheta: "Rânduri importate", valoare: sumar?.randuri },
          { eticheta: "Firme potrivite", valoare: sumar?.firme_potrivite },
          {
            eticheta: "Firme în scop",
            valoare: sumar?.firme_in_scop,
            ton: "bun",
          },
          {
            eticheta: "Firme fără dimensiune",
            valoare: sumar?.firme_fara_dimensiune,
            ton: "atentie",
            detaliu: "fără bilanț în export",
          },
          {
            eticheta: "Actualizare automată",
            valoare: sumar?.automate,
            detaliu:
              (sumar?.automate_eroare ?? 0) > 0
                ? `${formatNumar(sumar?.automate_eroare ?? 0)} cu eroare la ultima rulare`
                : "liste reluate lunar de la sursă",
            ton: (sumar?.automate_eroare ?? 0) > 0 ? "rau" : "bun",
          },
          {
            eticheta: "De actualizat",
            valoare: sumar?.de_actualizat,
            ton: (sumar?.de_actualizat ?? 0) > 0 ? "rau" : "bun",
            detaliu: "active, mai vechi de 30 de zile",
          },
        ]}
        actiuni={
          <>
            <Button
              variant={nrPropuneri > 0 ? "default" : "outline"}
              onClick={() => setPropuneri(true)}
            >
              <ListChecks className="mr-2 h-4 w-4" />
              De confirmat ({nrPropuneri})
            </Button>
            <Button variant="outline" onClick={() => setIstoric((v) => !v)}>
              <History className="mr-2 h-4 w-4" />
              Istoric ({date?.istoric.length ?? 0})
            </Button>
            <Button variant="outline" onClick={() => setAdauga(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Adaugă firmă pe listă
            </Button>
            <Button
              onClick={() =>
                setImportDialog({
                  deschis: true,
                  initial: tipFiltru
                    ? { tip: tipFiltru, inlocuieste: false }
                    : null,
                })
              }
            >
              <Upload className="mr-2 h-4 w-4" />
              Import / listă nouă
            </Button>
          </>
        }
      />

      {liste.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Listele nu au putut fi încărcate: {liste.error.message}
        </p>
      )}

      {istoric && (
        <IstoricModificari
          modificari={date?.istoric ?? []}
          titlu="Istoricul configurării listelor"
        />
      )}

      {date && date.versiuni.length > 0 && (
        <div className="rounded-md border">
          <p className="border-b px-3 py-2 text-sm font-medium">
            Ultimele actualizări
          </p>
          <ul className="divide-y text-sm">
            {date.versiuni.slice(0, 8).map((v) => (
              <li
                key={v.id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5"
              >
                <button
                  type="button"
                  className="truncate text-left hover:underline"
                  onClick={() => setVersiune(v.id)}
                >
                  <span className="font-mono text-xs text-muted-foreground">
                    {formatDataOra(v.importat_la)}
                  </span>{" "}
                  {v.nume_fisier}
                </button>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {ETICHETE_SURSA_VERSIUNE[v.sursa] ?? v.sursa} · +
                  {formatNumar(v.adaugate)} / −{formatNumar(v.eliminate)} ·{" "}
                  {formatNumar(v.randuri)} rânduri
                  {v.nepotrivite > 0 &&
                    ` · ${formatNumar(v.nepotrivite)} fără firmă`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <RaportInteractiv
        definitie={definitieRaportListe}
        randuri={randuri}
        isLoading={liste.isLoading}
        celula={celula}
        claseRand={(r) => (!r.activ ? "opacity-60" : undefined)}
        meniuRand={(r) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded p-1 text-muted-foreground hover:bg-muted"
                title="Acțiuni"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={() =>
                  setFirme({
                    criteriu: { tip: "lista", nume_fisier: r.nume_fisier },
                    titlu: `Firmele de pe lista „${r.nume_fisier}”`,
                  })
                }
              >
                Firmele de pe listă
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDetalii(r)}>
                Detalii și sursă
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!r.sursa_url}
                onClick={() =>
                  setImportDialog({
                    deschis: true,
                    initial: {
                      nume_fisier: r.nume_fisier,
                      url: r.sursa_url,
                      service_id: r.service_id,
                      tip: r.tip,
                      inlocuieste: true,
                      metoda: r.sursa_metoda,
                      pagina_token: r.sursa_pagina,
                      campuri: r.sursa_campuri,
                      coloana_serviciu: r.coloana_serviciu,
                      mapare_serviciu: r.mapare_serviciu,
                    },
                  })
                }
              >
                Actualizează de la sursă (în browser)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={
                  !r.sursa_url ||
                  actualizeaza.isPending ||
                  r.actualizare_stare === "in_curs"
                }
                onClick={() => actualizeazaPeServer(r)}
              >
                Actualizează acum pe server
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  setImportDialog({
                    deschis: true,
                    initial: {
                      nume_fisier: r.nume_fisier,
                      service_id: r.service_id,
                      tip: r.tip,
                      inlocuieste: true,
                      coloana_serviciu: r.coloana_serviciu,
                      mapare_serviciu: r.mapare_serviciu,
                    },
                  })
                }
              >
                Încarcă un fișier nou
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!r.ultima_versiune}
                onClick={() =>
                  r.ultima_versiune && setVersiune(r.ultima_versiune.id)
                }
              >
                Ultima versiune și diferențele
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setDeSters(r)}
              >
                Șterge lista
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      <FirmeCriteriuDialog deschis={firme} onClose={() => setFirme(null)} />
      <PropuneriPotrivireDialog
        deschis={propuneri}
        onClose={() => setPropuneri(false)}
      />
      <ImportListaDialog
        deschis={importDialog.deschis}
        onClose={() => setImportDialog({ deschis: false, initial: null })}
        fisiere={fisiere}
        servicii={servicii}
        initial={importDialog.initial}
        onVersiune={(id) => setVersiune(id)}
      />
      <AdaugaFirmaListaDialog
        deschis={adauga}
        onClose={() => setAdauga(false)}
        fisiere={fisiere}
        servicii={servicii}
      />
      <VersiuneListaDialog
        versiuneId={versiune}
        onClose={() => setVersiune(null)}
      />
      <DetaliiListaDialog
        lista={detalii}
        servicii={servicii}
        onClose={() => setDetalii(null)}
      />
      <AlertDialog
        open={deSters !== null}
        onOpenChange={(o) => !o && setDeSters(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Ștergi lista „{deSters?.nume_fisier}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Toate cele {formatNumar(deSters?.randuri ?? 0)} rânduri importate
              și configurarea listei vor fi eliminate. Firmele rămân în tabelul
              de încadrare, dar pierd potrivirea prin această listă la
              următoarea reîncadrare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Renunță</AlertDialogCancel>
            <AlertDialogAction onClick={confirmaStergerea}>
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
